import type { Thread } from '@atlas/api-contract';
import { MapPin, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { PendingPin } from '@/features/viewer/viewer-store';

import { CommentComposer } from './comment-composer';
import { toCreateThreadPayload } from './thread-schemas';
import { useCreateThread } from './threads-queries';

export interface NewThreadComposerProps {
  readonly pin: PendingPin;
  readonly versionId: string;
  /** Discards the pin entirely (spec R5.1). */
  readonly onCancel: () => void;
  /** The created thread, so its popover can open on the pin that just appeared. */
  readonly onCreated: (thread: Thread) => void;
}

/**
 * The composer that turns a comment-mode click into a thread (spec R5.1).
 *
 * ============================================================================
 * ONE REQUEST, AND CANCELLING LEAVES NOTHING BEHIND
 * ============================================================================
 * The anchor and the first comment go to the server together. There is no
 * "create the pin, then write in it": a pin with nothing said about it is not a
 * review comment, and a two-step create could leave one stranded on somebody
 * else's prototype if the second step failed or the reviewer walked away.
 *
 * Cancelling therefore has nothing to undo — the pin candidate lives only in
 * the viewer store, and clearing it is the whole of the discard. That is also
 * why the store drops it on a screen change and on leaving comment mode: the
 * anchor describes a click on a page the reviewer is no longer looking at.
 *
 * Authorship comes from the session now (Phase 2), so the submit handler just
 * posts the anchor and the first comment together; the server records who wrote
 * it. Any signed-in user may open a thread (spec matrix).
 */
export const NewThreadComposer = ({
  pin,
  versionId,
  onCancel,
  onCreated,
}: NewThreadComposerProps) => {
  const createThread = useCreateThread();

  const handleSubmit = async (body: string): Promise<boolean> => {
    const thread = await createThread.mutateAsync({
      versionId,
      payload: toCreateThreadPayload(pin, body),
    });

    onCreated(thread);
    return true;
  };

  return (
    <Card className="w-80 overflow-hidden shadow-lg">
      <header className="flex items-center gap-2 px-3 pt-3">
        <MapPin aria-hidden="true" className="size-3.5 text-pin" />
        <p className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
          New comment
          {pin.anchorText === null ? '' : ` on “${pin.anchorText}”`}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X aria-hidden="true" className="size-4" />
          <span className="sr-only">Discard this pin</span>
        </Button>
      </header>

      <div className="px-3 pb-3 pt-2">
        <CommentComposer
          label="Your comment"
          placeholder="What needs changing here?"
          submitLabel="Comment"
          pendingLabel="Posting…"
          autoFocus
          rows={3}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          cancelLabel="Discard"
        />
      </div>
    </Card>
  );
};
